import React, { useState, useMemo } from 'react';
import DepartmentCard from './DepartmentCard';

function DepartmentGrid({
  departments,
  onViewDepartment,
  darkMode: d,
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [activityFilter, setActivityFilter] = useState('all');

  const getActivityLevel = (total) => {
    if (total === 0) return 'low';
    if (total >= 100) return 'high';
    if (total >= 50) return 'active';
    return 'growing';
  };

  const filtered = useMemo(() => {
    return departments.filter((dept) => {
      const matchSearch = (dept.name || "").toLowerCase().includes(searchTerm.toLowerCase());
      const activity = getActivityLevel(dept.totalEntries);

      if (activityFilter === 'high') return matchSearch && activity === 'high';
      if (activityFilter === 'low') return matchSearch && (activity === 'low' || activity === 'growing');
      return matchSearch;
    });
  }, [departments, searchTerm, activityFilter]);

  return (
    <div>
      {/* Search & Filter Bar */}
      <div
        style={{
          display: 'flex',
          gap: '12px',
          alignItems: 'center',
          marginBottom: '24px',
          flexWrap: 'wrap',
        }}
      >
        <div
          style={{
            flex: 1,
            minWidth: '250px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: d ? '#1e293b' : '#f8fafc',
            border: `1.5px solid ${d ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`,
            borderRadius: '10px',
            padding: '8px 16px',
          }}
        >
          <span style={{ fontSize: '16px' }}>🔍</span>
          <input
            type="text"
            placeholder="Search by department name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              background: 'transparent',
              border: 'none',
              outline: 'none',
              fontSize: '14px',
              color: d ? '#e2e8f0' : '#334155',
              width: '100%',
              fontWeight: '500',
            }}
          />
        </div>

        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            onClick={() => setActivityFilter('all')}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: `1.5px solid ${activityFilter === 'all' ? '#2563eb' : d ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`,
              background: activityFilter === 'all' ? '#2563eb' : d ? 'rgba(255,255,255,0.04)' : '#fff',
              color: activityFilter === 'all' ? '#fff' : d ? '#e2e8f0' : '#334155',
              fontWeight: '700',
              fontSize: '14px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              if (activityFilter !== 'all') {
                e.currentTarget.style.borderColor = '#94a3b8';
              }
            }}
            onMouseLeave={(e) => {
              if (activityFilter !== 'all') {
                e.currentTarget.style.borderColor = d ? 'rgba(255,255,255,0.1)' : '#e2e8f0';
              }
            }}
          >
            All
          </button>
          <button
            onClick={() => setActivityFilter('high')}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: `1.5px solid ${activityFilter === 'high' ? '#dc2626' : d ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`,
              background: activityFilter === 'high' ? '#dc2626' : d ? 'rgba(255,255,255,0.04)' : '#fff',
              color: activityFilter === 'high' ? '#fff' : d ? '#e2e8f0' : '#334155',
              fontWeight: '700',
              fontSize: '14px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              if (activityFilter !== 'high') {
                e.currentTarget.style.borderColor = '#94a3b8';
              }
            }}
            onMouseLeave={(e) => {
              if (activityFilter !== 'high') {
                e.currentTarget.style.borderColor = d ? 'rgba(255,255,255,0.1)' : '#e2e8f0';
              }
            }}
          >
            High Activity
          </button>
          <button
            onClick={() => setActivityFilter('low')}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: `1.5px solid ${activityFilter === 'low' ? '#16a34a' : d ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`,
              background: activityFilter === 'low' ? '#16a34a' : d ? 'rgba(255,255,255,0.04)' : '#fff',
              color: activityFilter === 'low' ? '#fff' : d ? '#e2e8f0' : '#334155',
              fontWeight: '700',
              fontSize: '14px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              if (activityFilter !== 'low') {
                e.currentTarget.style.borderColor = '#94a3b8';
              }
            }}
            onMouseLeave={(e) => {
              if (activityFilter !== 'low') {
                e.currentTarget.style.borderColor = d ? 'rgba(255,255,255,0.1)' : '#e2e8f0';
              }
            }}
          >
            Low Activity
          </button>
        </div>
      </div>

      {/* Department Cards Grid */}
      {filtered.length === 0 ? (
        <div
          style={{
            textAlign: 'center',
            padding: '60px 24px',
            color: d ? '#94a3b8' : '#94a3b8',
          }}
        >
          <div style={{ fontSize: '48px', marginBottom: '12px' }}>🏢</div>
          <p style={{ fontSize: '16px', fontWeight: '600' }}>
            {departments.length === 0
              ? 'No departments found'
              : 'No departments match your search'}
          </p>
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            gap: '20px',
          }}
        >
          {filtered.map((dept) => (
            <DepartmentCard
              key={dept.id}
              department={dept}
              onViewAll={onViewDepartment}
              darkMode={d}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default DepartmentGrid;
